<template>
  <div class="mb-5 mt-3">
    <v-divider></v-divider>
    <v-tabs
      class="notes-tabs"
      v-model="activeTab"
      show-arrows
      color="transparent"
      slider-color="primary">
      <v-tab>Model Notes ({{ currentDate | readableDate }})</v-tab>
      <v-tab v-if="pollingData">Polling</v-tab>
      <v-tab class="fec-tab">FEC</v-tab>
      <v-tab v-if="variables">Feature Importance</v-tab>
      <v-tab>Past Elections</v-tab>
    </v-tabs>
    <v-divider></v-divider>
    <v-tabs-items v-model="activeTab" touchless class="mt-2">
      <v-tab-item key="model-notes-tab">
        <!-- <a href="http://localhost:5656/data-dictionary?name=audjusted_pvi">link</a> -->
        <template v-if="Object.keys(categoryBulletPoints).length === 0">
          <div class="bullet-points" v-html="movementBulletPoints(models, 'gopWinProbability')"></div>
          <div class="bullet-points" v-html="currentModel.bulletPoints"></div>
        </template>
        <div v-else>
          <v-btn-toggle v-model="category" style="margin: 10px 0 15px 0;">
            <v-btn value="overview" flat small class="btn-toggle-option" :color="category === 'overview' ? 'red' : 'grey darken-4'">
              Overview
            </v-btn>
            <v-btn value="change" flat small class="btn-toggle-option" :color="category === 'change' ? 'red' : 'grey darken-4'">
              Change
            </v-btn>
            <v-btn value="variables" flat small class="btn-toggle-option" :color="category === 'variables' ? 'red' : 'grey darken-4'">
              Variables
            </v-btn>
          </v-btn-toggle>
          <div>

          </div>
          <div style="width: 100%;">
            <div v-if="category === 'overview'">
              <ul>
                <div v-for="(item, index) in categoryBulletPoints.overview" :key="index">
                  <li v-html="item"></li>
                </div>
              </ul>
            </div>
            <div v-if="category === 'change'">
              <ul>
                <div v-for="(item, index) in categoryBulletPoints.change" :key="index">
                  <li v-html="item"></li>
                </div>
              </ul>
            </div>
            <div v-if="category === 'variables'">
              <ul>
                <div v-for="(item, index) in categoryBulletPoints.variables" :key="index">
                  <bullet-point :item="item"></bullet-point>
                </div>
              </ul>
            </div>
          </div>
        </div>
      </v-tab-item>
      <v-tab-item key="polling-notes-tab" style="margin-top: 20px;" v-if="pollingData">
        <div style="display: flex; justify-content: space-between;" class="mb-2">
          <span class="margin-average" v-if="marginAvg">
            <b class="mr-2">Smart Average:</b>
            <span :style="marginStyle(smartMargin)">{{ smartMargin }}</span>
          </span>
          <span class="margin-average" v-if="marginAvg">
            <b class="mr-2">Simple Average:</b>
            <span :style="marginStyle(marginAvg)">{{ marginAvg }}</span>
          </span>
        </div>
        <v-data-table
          :headers="pollingHeaders"
          :items="pollingData"
          :pagination.sync="pollingPagination">
          <template slot="items" slot-scope="props">
            <tr v-if="typeof props.item === 'object'">
              <td>
                <a style="color: #66ABCF; text-decoration: underline;" :href="props.item.scraped_url">{{ props.item.pollster }}</a>
                <span :style="props.item.pollster_affiliation === 'D' ? 'color: #66ABCF;' : 'color: #FA3138;'" v-if="props.item.pollster_affiliation">({{ props.item.pollster_affiliation }})</span>
                <i class="boolean-icons material-icons" style="color: #57AEA6; font-size: 1em;" v-if="props.item.include">done</i>
              </td>
              <td>{{ props.item.start_date | monthDay }}</td>
              <td>{{ props.item.end_date | monthDay }}</td>
              <td>{{ props.item.sample_size | commas }}</td>
              <td>{{ props.item.gop_pct }}%</td>
              <td>{{ props.item.dem_pct }}%</td>
              <td>{{ props.item.other_pct }}%</td>
              <td :style="getMarginColor(props.item.margin)">{{ props.item.margin | margin }}</td>
              <td>{{ props.item.margin_of_error }}</td>
            </tr>
          </template>
        </v-data-table>
        <div style="display: flex;" class="mt-4">
          <i class="boolean-icons material-icons" style="color: #57AEA6; font-size: 0.9em;">done</i>
          <span style="font-size: 0.75em;">= Included in model, N = Sample, GOP = Republican, Dem = Democrat, Oth = Other, M = Margin, MoE = Margin of Error</span>
        </div>
        <div style="display: flex; justify-content: flex-end;">
          <span class="last-updated"><b class="mr-2">Last Updated:</b> {{ election.dateModified | datetime }}</span>
        </div>
      </v-tab-item>
      <v-tab-item key="fec-notes-tab">
        <v-data-table
          :headers="fecHeaders"
          :items="currentElectionData"
          :pagination.sync="fecPagination"
          hide-actions>
          <template slot="items" slot-scope="props">
            <td><a style="color: #66ABCF; text-decoration: underline;" :href="props.item.fec_url">{{ props.item.candidate_name }}</a></td>
            <td>{{ props.item.party }}</td>
            <td>{{ props.item.cash_on_hand | money }}</td>
            <td>{{ props.item.total_raised | money }}</td>
            <td>{{ props.item.total_spent | money }}</td>
            <td>{{ props.item.ind_exp_support | money }}</td>
            <td>{{ props.item.ind_exp_oppose | money }}</td>
          </template>
        </v-data-table>
        <div style="font-size: 0.75em;">IE = Independent Expenditures, CoH = Cash on Hand</div>
        <div class="last-updated">Last Updated: {{ election.dateModified | datetime }}</div>
      </v-tab-item>
      <v-tab-item key="variables-notes-tab">
        <template v-for="(feat, index) in variables">
          <div :key="index" class="variables" style="text-align: start; margin-left: 10px;">
            <span><b>{{feat[0]}}: </b></span>
            <span>{{feat[1]}}%</span>
          </div>
        </template>
        <v-btn small outline class="mt-2 mr-2" color="red" @click="$router.push({ name: 'data_dictionary' })">Go To Data Dictionary</v-btn>
      </v-tab-item>
      <v-tab-item key="past-elections-notes-tab">
        <v-layout row wrap>
          <v-flex xs12 md6 style="padding: 10px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
              <span><b>2016 Presidential</b></span>
              <span style="font-size: 0.9em;"><b>Total Votes: </b>{{ electionJson['2016_presidential'].turnout }}</span>
            </div>
            <v-data-table
              :headers="presidential2016Headers"
              :items="presidential2016Data"
              :pagination.sync="presidential2016Pagiantion"
              hide-actions
              style="margin-bottom: 30px">
              <template slot="items" slot-scope="props">
                <tr v-if="typeof props.item === 'object'">
                  <td>{{ props.item.name }}</td>
                  <td :style="getPartyStyle(getCandidateParty(props.item.name))">{{ getCandidateParty(props.item.name) }}</td>
                  <td>{{ props.item.count | commas }}</td>
                  <td>{{ props.item.pct }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex xs12 md6 style="padding: 10px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
              <span><b>
                <template v-if="chamber === 'Senate'">{{ senateElectionYear }}</template>
                <template v-if="chamber === 'House'">2016</template>
                {{ chamber }}</b></span>
              <span style="font-size: 0.9em;"><b>Total Votes:</b> {{ houseTotalVotes | commas }}</span>
            </div>
            <v-data-table
              :headers="pastElectionsHeaders"
              :items="pastElectionsData.filter(item => item.candidate_name)"
              :pagination.sync="pastElectionsPagination"
              hide-actions>
              <template slot="items" slot-scope="props">
                <tr :style="winner(props.item.wonElection)">
                  <td>{{ props.item.candidate_name }}</td>
                  <td :style="getPartyStyle(props.item.party.charAt(0))">{{ props.item.party | initial }}</td>
                  <td>{{ props.item.candidate_vote_count | commas }}</td>
                  <td>{{ props.item.candidate_vote_pct | votePercent }}</td>
                </tr>
              </template>
            </v-data-table>
            <div style="display: flex; justify-content: flex-end;">
              <span class="last-updated"><b class="mr-2">Last Updated:</b> {{ election.dateModified | datetime }}</span>
            </div>
          </v-flex>
        </v-layout>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { OptiCard } from '@/components/optimus'
import BulletPoint from './BulletPoint'

const props = {
  election: {
    type: Object,
    required: true
  },
  currentModel: {
    type: Object,
    required: true
  },
  currentDate: {
    type: String,
    required: true
  },
  models: {
    type: Array,
    required: true
  }
}

const computed = {
  ...mapGetters({
    dates: 'aggregateModels/dates',
    mobile: 'ux/mobile'
  }),
  electionJson () {
    return JSON.parse(this.election.electionJson)
  },
  currentElectionData () {
    const election = this.electionJson.current_election
    return Object.values(election).filter(item => item.candidate_name)
  },
  presidential2016Data () {
    const election = this.electionJson['2016_presidential']
    return Object.values(election)
  },
  pastElectionsData () {
    const elections = this.electionJson.previous_election
    return Object.values(elections)
  },
  senateElectionYear () {
    return this.pastElectionsData.filter(item => typeof item === 'number')[0]
  },
  houseTotalVotes () {
    let votes = this.pastElectionsData.map(item => item.candidate_vote_count)
    votes = votes.filter(item => typeof item !== 'undefined')
    if (votes.length > 0) {
      return votes.reduce((a, b) => { return a + b })
    }
  },
  pollingData () {
    let polls = this.electionJson.polls
    return Object.values(polls)
  },
  variables () {
    let features = []
    Object.entries(this.currentModel.features).forEach(item => {
      const arr = []
      arr[0] = item[0]
      arr[1] = parseFloat(item[1]).toFixed(2)
      features.push(arr)
    })
    features.sort((a, b) => { return b[1] - a[1] })
    return features
  },
  categoryBulletPoints () {
    return this.currentModel.categoryBulletPoints
  },
  marginAvg () {
    return this.electionJson.polls.margin_avg
  },
  smartMargin () {
    return this.electionJson.polls.smart_margin_avg
  },
  chamber () {
    if (this.election.cd) {
      return 'House'
    } else {
      return 'Senate'
    }
  }
}

const methods = {
  movementBulletPoints (models, key) {
    const texts = []
    const index = this.dates.indexOf(this.currentDate)
    const current = models.find(model => model.date === this.currentDate)
    const prev = models.find(model => model.date === this.dates[index - 1])
    if (prev && index > 0) {
      let diff = Math.round((current[key] - prev[key]) * 10000) / 100
      diff = diff.toString()
      let party = null
      if (diff.includes('-')) {
        party = 'Democratic'
        diff = diff.replace('-', '')
      } else {
        party = 'Republican'
      }
      texts.push(
        `Moved ${diff}% towards the ${party} candidate in this race since the last model`
      )
    }
    return `<ul>${texts.map(text => `<li>${text}</li>`)}</ul>`
  },
  winner (won) {
    if (won) return 'background-color: rgba(87, 174, 166, 0.2);'
  },
  getMarginColor (value) {
    if (value === 0) {
      return 'color: #57AEA6;'
    } else if ((value.toString()).includes('-')) {
      return 'color: #66ABCF;'
    } else {
      return 'color: #FA3138;'
    }
  },
  getCandidateParty (name) {
    if (name.includes('Trump')) {
      return 'R'
    } else if (name.includes('Clinton')) {
      return 'D'
    } else {
      return 'Other'
    }
  },
  getPartyStyle (party) {
    if (party === 'D') {
      return 'color: #66ABCF; font-weight: 500;'
    } else if (party === 'R') {
      return 'color: #FA3138; font-weight: 500;'
    } else {
      return 'color: #57AEA6; font-weight: 500;'
    }
  },
  marginStyle (margin) {
    if (margin.includes('D')) {
      return 'color: #66ABCF; font-style: normal; font-size: 1.2em; font-weight: 600;'
    } else if (margin.includes('R')) {
      return 'color: #FA3138; font-style: normal; font-size: 1.2em; font-weight: 600;'
    }
  }
}

const components = {
  OptiCard,
  BulletPoint
}

export default {
  props,
  computed,
  methods,
  components,
  data () {
    return {
      activeTab: '',
      category: 'overview',
      pollingPagination: {
        sortBy: 'end_date',
        descending: true,
        rowsPerPage: 10
      },
      fecPagination: {
        sortBy: 'total_raised',
        descending: true,
        rowsPerPage: 10
      },
      pastElectionsPagination: {
        sortBy: 'candidate_vote_count',
        descending: true,
        rowsPerPage: 10
      },
      presidential2016Pagiantion: {
        sortBy: 'count',
        descending: true,
        rowsPerPage: 10
      },
      pollingHeaders: [
        { text: 'Poll', value: 'pollster', width: '25%' },
        { text: 'Start Date', value: 'start_date', width: '10%' },
        { text: 'End Date', value: 'end_date', width: '10%' },
        { text: 'N', value: 'sample', width: '8%' },
        { text: 'GOP', value: 'gop_pct', width: '8%' },
        { text: 'Dem', value: 'dem_pct', width: '8%' },
        { text: 'Oth', value: 'other_pct', width: '8%' },
        { text: 'M', value: 'margin', width: '8%' },
        { text: 'MoE', value: 'margin_of_error', width: '8%' }
      ],
      fecHeaders: [
        { text: 'Candidate', value: 'candidate_name' },
        { text: 'Party', value: 'party' },
        { text: 'CoH', value: 'cash_on_hand' },
        { text: 'Contributions', value: 'total_raised' },
        { text: 'Disbursements', value: 'total_spent' },
        { text: 'IE (Support)', value: 'ind_exp_support' },
        { text: 'IE (Oppose)', value: 'ind_exp_oppose' }
      ],
      pastElectionsHeaders: [
        { text: 'Candidate', value: 'candidate' },
        { text: 'Party', value: 'party' },
        { text: 'Vote Total', value: 'candidate_vote_count' },
        { text: 'Vote %', value: 'candidate_vote_pct' }
      ],
      presidential2016Headers: [
        { text: 'Candidate', value: 'name' },
        { text: 'Party', value: 'party' },
        { text: 'Vote Total', value: 'count' },
        { text: 'Vote %', value: 'pct' }
      ]
    }
  }
}
</script>

<style>
.margin-average, .last-updated {
  font-size: 0.8em;
}

.last-updated {
  font-style: italic;
}

.variables {
  font-size: 1em;
}

.notes-tabs .tabs__bar .tabs__wrapper .tabs__container {
  height: 40px !important;
}

@media only screen and (min-width: 700px) {
  table {
    table-layout: fixed !important;
    width: 100% !important;
  }
}

@media only screen and (max-width: 420px) {
  .variables  {
    font-size: 0.8em;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/detail/Notes.vue